import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import MenuIcon from "@mui/icons-material/Menu";
import {
  AppBar,
  Button,
  Container,
  Divider,
  Drawer,
  drawerClasses,
  IconButton,
  Stack,
  Toolbar,
  Typography
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { logout } from "../../api/auth";
import { clearNotifications } from "../../store/Notification";
import { resetSessionAuth } from "../../store/Session";
import { StoreInterfaces } from "../../types/store/StoreInterfaces";
import LanguagePicker from "./LanguageSelectors/LanguagePicker";
import LanguageToggle from "./LanguageSelectors/LanguageToggle";
import ThemeIconButton from "./ThemeSelectors/ThemeIconButton";
import ThemeToggle from "./ThemeSelectors/ThemeToggle";

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexShrink: 0,
  borderRadius: `calc(${theme.shape.borderRadius}px + 8px)`,
  backdropFilter: "blur(24px)",
  border: "1px solid",
  borderColor: (theme.vars || theme).palette.divider,
  backgroundColor: theme.vars
    ? `rgba(${theme.vars.palette.background.defaultChannel} / 0.4)`
    : alpha(theme.palette.background.default, 0.4),
  boxShadow: (theme.vars || theme).shadows[1],
  padding: "8px 12px",
}));

const sectionLabelSx = {
  color: "text.secondary",
  textTransform: "uppercase",
  fontWeight: 600,
  letterSpacing: 0.5,
};

export default function PublicNavbar() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const session = useSelector((state: StoreInterfaces) => state.session);
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const isLoggedIn = Boolean(session?.accessToken);
  const homePath = isLoggedIn ? "/home" : "/";

  const handleLogoutClick = () => {
    logout()
      .then(() => {
        dispatch(resetSessionAuth());
        dispatch(clearNotifications());
      })
      .catch((error) => {
        console.error(error);
        dispatch(resetSessionAuth());
        dispatch(clearNotifications());
      })
      .finally(() => setMobileOpen(false));
  };

  return (
    <AppBar
      position="fixed"
      enableColorOnDark
      sx={{
        boxShadow: 0,
        bgcolor: "transparent",
        backgroundImage: "none",
        mt: "calc(var(--template-frame-height, 0px) + 28px)",
      }}
    >
      <Container>
        <StyledToolbar variant="dense" disableGutters>
          {/* Desktop */}
          <Stack
            direction="row"
            spacing={1}
            sx={{ display: { xs: "none", md: "flex" } }}
          >
            <Button
              color="primary"
              size="small"
              onClick={() => navigate(homePath)}
            >
              {t("generic.home")}
            </Button>
            <Button
              color="primary"
              size="small"
              onClick={() => navigate("/licenses")}
            >
              {t("licenses.licenses")}
            </Button>
          </Stack>

          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ display: { xs: "none", md: "flex" } }}
          >
            <LanguagePicker />

            {isLoggedIn && <ThemeIconButton />}

            {isLoggedIn ? (
              <Button
                color="primary"
                size="small"
                startIcon={<LogoutRoundedIcon />}
                onClick={handleLogoutClick}
              >
                {t("generic.logout")}
              </Button>
            ) : (
              <Button
                color="primary"
                size="small"
                variant="contained"
                onClick={() => navigate("/login")}
              >
                {t("generic.login.title")}
              </Button>
            )}
          </Stack>

          {/* Mobile */}
          <Stack
            direction="row"
            sx={{
              display: { xs: "flex", md: "none" },
              width: "100%",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Button
              color="primary"
              size="small"
              onClick={() => navigate(homePath)}
            >
              {t("generic.home")}
            </Button>

            <IconButton
              aria-label="Menu button"
              onClick={() => setMobileOpen(true)}
            >
              <MenuIcon />
            </IconButton>

            <Drawer
              anchor="right"
              open={mobileOpen}
              onClose={() => setMobileOpen(false)}
              sx={{
                [`& .${drawerClasses.paper}`]: {
                  width: 280,
                  maxWidth: "85vw",
                  backgroundImage: "none",
                  backgroundColor: "background.paper",
                },
              }}
            >
              <Stack sx={{ height: "100%", justifyContent: "space-between" }}>
                <Stack sx={{ p: 2 }}>
                  <Button
                    fullWidth
                    sx={{ justifyContent: "flex-start", px: 1 }}
                    onClick={() => {
                      setMobileOpen(false);
                      navigate("/licenses");
                    }}
                  >
                    {t("licenses.licenses")}
                  </Button>
                </Stack>

                <Stack>
                  <Divider />

                  <Stack spacing={2} sx={{ p: 2 }}>
                    <Stack spacing={0.5}>
                      <Typography variant="caption" sx={sectionLabelSx}>
                        {t("generic.language")}
                      </Typography>
                      <LanguageToggle />
                    </Stack>

                    {isLoggedIn && (
                      <Stack spacing={0.5}>
                        <Typography variant="caption" sx={sectionLabelSx}>
                          {t("generic.appearance")}
                        </Typography>
                        <ThemeToggle />
                      </Stack>
                    )}
                  </Stack>

                  <Divider />

                  <Stack sx={{ p: 2 }}>
                    {isLoggedIn ? (
                      <Button
                        variant="outlined"
                        color="error"
                        fullWidth
                        startIcon={<LogoutRoundedIcon />}
                        onClick={handleLogoutClick}
                      >
                        {t("generic.logout")}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => {
                          setMobileOpen(false);
                          navigate("/login");
                        }}
                      >
                        {t("generic.login.title")}
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </Stack>
            </Drawer>
          </Stack>
        </StyledToolbar>
      </Container>
    </AppBar>
  );
}
